import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import Heading from '@atlaskit/heading';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type ComponentTypesMetadata from '@atlassian/jira-compass-common/src/common/ui/component-types-metadata/index.tsx';
import { getIdFromAri } from '@atlassian/jira-compass-common/src/common/utils.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { ISSUES, NAME } from '../../common/constants/order-fields.tsx';
import type { Component } from '../../common/model/types.tsx';
import { getTeams } from '../../common/rest/teams/index.tsx';
import type { TeamRecord } from '../../common/rest/teams/types.tsx';
import { useGetCompassComponents } from '../../common/services/search-compass-components/index.tsx';
import type { CompassComponent } from '../../common/services/search-compass-components/types.tsx';
import type { OrderDirection, OrderField } from '../../query-param-adapter/model/order.tsx';
import ComponentCell from './cells/component/view.tsx';
import DependenciesCell from './cells/dependencies/view.tsx';
import IssuesCell from './cells/issues/index.tsx';
import OwnerCell from './cells/owner/view.tsx';
import { RepositoryCell } from './cells/repository/view.tsx';
import ScorecardCell from './cells/scorecard/view.tsx';
import { ComponentsTableErrorState } from './error-state/components-table-error-state.tsx';
import messages from './messages.tsx';

type Props = {
	testId?: string;
	projectKey: string;
	isPageLoading: boolean;

	componentIds: string[];
	componentsById: Record<string, Component>;
	componentAris: string[];

	componentCount: number;
	orderField: OrderField;
	orderDirection: OrderDirection;
	onSort?: (sort: { key: OrderField; sortOrder: OrderDirection }) => void;

	children?: React.ReactNode;
};

const useGetTeams = (
	components: CompassComponent[],
	siteId: String,
): { teamInfo: TeamRecord; error: Error | undefined } => {
	const [teamInfo, setTeamInfo] = useState<TeamRecord>({});
	const [error, setError] = useState<Error | undefined>(undefined);

	useEffect(() => {
		if (components.length === 0) {
			setTeamInfo({});
			return;
		}

		const ownerIds = Array.from(
			new Set(
				components
					.map((component) => component?.ownerId)
					.filter(Boolean)
					.map(getIdFromAri),
			),
		);

		if (ownerIds.length === 0) {
			setTeamInfo({});
			return;
		}

		getTeams(ownerIds, siteId)
			.then((result) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const teamsByAri = Object.fromEntries(
					result.teams.map((team) => [team.id, team]),
				) as TeamRecord;
				setTeamInfo(teamsByAri);
			})
			.catch((e) => setError(e));
	}, [components, siteId]);

	useEffect(() => {
		if (error) {
			fireErrorAnalytics({
				meta: {
					id: 'useGetTeams',
					packageName: 'jiraProjectDirectoriesComponents',

					teamName: 'compass-lodestone',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [error]);

	return { teamInfo, error };
};

export default function CompassComponentsTable(props: Props) {
	const {
		testId,
		projectKey,
		isPageLoading,
		componentIds,
		componentsById,
		componentAris,
		componentCount,
		orderField,
		orderDirection,
		onSort,
		children,
	} = props;
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const siteId = useCloudId();

	const {
		components: compassComponents,
		loading: compassComponentsLoading,
		error: compassComponentsError,
	} = useGetCompassComponents(componentAris);
	const loading = useMemo(
		() => compassComponentsLoading || isPageLoading,
		[compassComponentsLoading, isPageLoading],
	);

	// Merged list of components in Jira that also exist in Compass.
	// This list is also sorted correctly via Jira.
	const mergedComponents = useMemo(() => {
		if (loading || compassComponents.length === 0) {
			return [];
		}

		// We reduce by `componentIds` as these are in correct sort order from server already.
		return componentIds.reduce<(CompassComponent & { issueCount: number })[]>(
			(acc, componentId) => {
				// Get the full Jira component from `componentsById`. We need the ARI and issue count.
				const jiraComponent = componentsById[componentId];
				// Find a matching component that exists in Compass with the same ARI.
				const compassComponent = compassComponents.find(
					(curCompassComponent) => curCompassComponent.id === jiraComponent.ari,
				);

				// Component exists in compass, we can render it.
				if (compassComponent) {
					acc.push({
						...compassComponent,
						issueCount: jiraComponent.issueCount,
					});
				}

				return acc;
			},
			[],
		);
	}, [loading, componentIds, componentsById, compassComponents]);

	const { teamInfo } = useGetTeams(mergedComponents, siteId);

	const head = useMemo(
		() => ({
			cells: [
				{
					key: NAME,
					content: formatMessage(messages.componentHeader),
					width: 25,
					isSortable: true,
				},
				{
					key: 'owner-team',
					content: formatMessage(messages.ownerTeamHeader),
					width: 25,
					isSortable: false,
				},
				{
					key: 'repository',
					content: formatMessage(messages.repositoryHeader),
					width: 18,
					isSortable: false,
				},
				{
					key: 'dependencies',
					content: formatMessage(messages.dependenciesHeader),
					width: 12,
					isSortable: false,
				},
				{
					key: 'scorecard',
					content: formatMessage(messages.scorecardHeader),
					width: 12,
					isSortable: false,
				},
				{
					key: ISSUES,
					content: formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.issuesHeaderIssueTermRefresh
							: messages.issuesHeader,
					),
					width: 8,
					isSortable: true,
				},
			],
		}),
		[formatMessage],
	);

	const rows = mergedComponents.map((component, idx) => {
		const ownerId = getIdFromAri(component?.ownerId ?? '');

		return compassComponentToRow(
			{
				...component,
				ownerName: teamInfo[ownerId]?.displayName ?? null,
			},
			projectKey,
			// Sorting is handled by API.
			orderDirection === 'DESC' ? -(idx + 1) : idx + 1,
		);
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onClickLearnMore = (_: any, analyticEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticEvent, 'learnMoreAboutLinkingIssuesToCompassLink');
	};

	const content = (
		<>
			<Box paddingBlockEnd="space.500">
				<Flex alignItems="center">
					<Heading size="small">
						{formatMessage(messages.componentCount, {
							componentCount,
						})}
					</Heading>
					<Popup
						isOpen={isOpen}
						onClose={() => setIsOpen(false)}
						placement="right-start"
						messageId="project-directories.components.compass-table.popup"
						messageType="transactional"
						content={() => (
							<Box padding="space.200" xcss={tableHeaderStyles}>
								{formatMessage(
									fg('jira-issue-terminology-refresh-m3')
										? messages.componentInfoIssueTermRefresh
										: messages.componentInfo,
									{
										// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
										bold: (text: React.ReactNode) => <strong>{text}</strong>,
										link: (text: React.ReactNode) => (
											<HelpPanelButton
												articleId="6frUzedhV1M0c05JhXMvK0"
												appearance="link"
												spacing="none"
												onClick={onClickLearnMore}
												fallbackComponent={
													<Button
														href="https://support.atlassian.com/jira-software-cloud/docs/link-issues-to-compass-components/"
														target="_blank"
														appearance="link"
														spacing="none"
														onClick={onClickLearnMore}
													>
														{text}
													</Button>
												}
											>
												{text}
											</HelpPanelButton>
										),
									},
								)}
							</Box>
						)}
						trigger={(triggerProps) => {
							if (fg('_jira-concurrent-state-update-hasnt-mounted-yet')) {
								return (
									<IconButton
										icon={EditorPanelIcon}
										label=""
										spacing="compact"
										appearance="subtle"
										testId="project-directories.components.compass-table.add"
										onClick={() => setIsOpen(!isOpen)}
										{...triggerProps}
									/>
								);
							}
							return (
								<Button
									spacing="compact"
									appearance="subtle-link"
									iconBefore={<EditorPanelIcon spacing="spacious" label="" />}
									testId="project-directories.components.compass-table.add"
									onClick={() => setIsOpen(!isOpen)}
									{...triggerProps}
								/>
							);
						}}
					/>
				</Flex>
			</Box>
			<TableWrapper data-testid={testId}>
				<DynamicTableStateless
					isFixedSize
					loadingSpinnerSize="large"
					head={head}
					rows={rows}
					isLoading={loading}
					sortKey={orderField}
					sortOrder={orderDirection}
					onSort={(data, analyticsEvent) => {
						onSort?.(data);

						if (analyticsEvent) {
							fireUIAnalytics(analyticsEvent, 'compassComponentsTableSorted', {
								key: data.key,
								sortOrder: data.sortOrder,
							});
						}
					}}
					emptyView={compassComponentsError ? <ComponentsTableErrorState /> : undefined}
				/>
			</TableWrapper>
		</>
	);

	return (
		<UFOSegment name="global-components">
			<JSErrorBoundary
				fallback="unmount"
				packageName="jiraProjectDirectoriesComponents"
				teamName="compass-lodestone"
				id="compassComponentsTable"
				sendToPrivacyUnsafeSplunk
			>
				{content}
			</JSErrorBoundary>
			{!compassComponentsError && children}
		</UFOSegment>
	);
}

function compassComponentToRow(
	component: CompassComponent & { ownerName?: string; issueCount: number },
	projectKey: string,
	key: number,
) {
	const componentId = getIdFromAri(component.id);
	const ownerId = component.ownerId ? getIdFromAri(component?.ownerId ?? '') : undefined;

	return {
		cells: [
			{
				content: (
					<ComponentCell
						componentId={componentId}
						name={component.name}
						description={component.description}
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						typeId={component.typeId as keyof typeof ComponentTypesMetadata}
						isArchived={component.state === 'ARCHIVED'}
					/>
				),
				key,
			},
			{
				content: <OwnerCell ownerName={component.ownerName} ownerId={ownerId} />,
				key,
			},
			{
				content: <RepositoryCell repositoryLinks={component.links} />,
				key,
			},
			{
				content: (
					<DependenciesCell
						componentId={componentId}
						inwardRelationships={component.inwardRelationships}
						outwardRelationships={component.outwardRelationships}
					/>
				),
				key,
			},
			{
				content: (
					<ScorecardCell
						// Use full component ARI to make scorecards query.
						componentId={component.id}
						scorecardScores={component.scorecardScores}
					/>
				),
				key,
			},
			{
				content: (
					<IssuesCell
						projectKey={projectKey}
						componentName={component.name}
						issueCount={component.issueCount}
					/>
				),
				key,
			},
		],
	};
}

const tableHeaderStyles = xcss({
	width: '417px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'tbody > tr > td': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderTop: `1px solid ${token('color.border')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'tbody > tr > td > a': {
		alignItems: 'center',
	},
});
